import React from 'react';
import jsQR from 'jsqr';
import { parseGirocode } from 'Components/Banking/girocode';
import './BankSyncModal.css';
import './GirocodeScanModal.css';

// Live-Kamera-Scan eines Girocodes (EPC-QR-Code, ueblich auf deutschen
// Rechnungen). Zieht Frames per requestAnimationFrame aus dem Video-Stream in
// einen unsichtbaren Canvas und laesst jsQR (reines JS, keine native
// Abhaengigkeit) pro Frame nach einem QR-Code suchen - kein Server-Roundtrip,
// alles laeuft lokal im Browser.
//
// Bewusst KEINE facingMode-Vermutung ("environment"/"user") mehr - das passt
// nicht auf Continuity Camera (iPhone als Mac-Webcam hat kein Frontkamera-
// Konzept), Safari/Chrome waehlten dadurch stillschweigend die eingebaute
// Mac-Kamera statt das iPhone anzubieten (nur Firefox war hier toleranter).
// Stattdessen: einmal generisch Kamera-Berechtigung einholen (erst danach
// liefert enumerateDevices() ueberhaupt Klartext-Geraetenamen), dann echte
// Geraeteauswahl anzeigen - mit Auto-Vorauswahl eines Geraets, dessen Name
// "iPhone" enthaelt, falls vorhanden.
export default function GirocodeScanModal(props) {

	const videoRef = React.useRef(null);
	const canvasRef = React.useRef(null);
	const streamRef = React.useRef(null);
	const rafRef = React.useRef(null);
	const stoppedRef = React.useRef(false);
	const [error, setError] = React.useState('');
	const [devices, setDevices] = React.useState(null);
	const [deviceId, setDeviceId] = React.useState('');

	// Schritt 1: einmalig generisch um Kamera-Berechtigung bitten und danach
	// die Geraeteliste (mit Klartext-Labels) einlesen.
	React.useEffect(() => {
		let cancelled = false;

		if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
			setError('Kamerazugriff wird von diesem Browser nicht unterstützt.');
			return;
		}

		navigator.mediaDevices.getUserMedia({ video: true })
			.then((stream) => {
				stream.getTracks().forEach((t) => t.stop());
				if (cancelled) return;
				return navigator.mediaDevices.enumerateDevices();
			})
			.then((deviceList) => {
				if (cancelled || !deviceList) return;
				const videoInputs = deviceList.filter((d) => d.kind === 'videoinput');
				setDevices(videoInputs);
				const iphone = videoInputs.find((d) => /iphone/i.test(d.label));
				setDeviceId((iphone || videoInputs[0])?.deviceId || '');
			})
			.catch((e) => {
				setError('Kamera konnte nicht geöffnet werden (' + e.message + '). Bitte Kamera-Berechtigung erlauben.');
			});

		return () => { cancelled = true; };
	}, []);

	// Schritt 2: sobald ein konkretes Geraet feststeht (automatisch oder per
	// Auswahl gewechselt), dessen Stream oeffnen und mit dem Scannen beginnen.
	React.useEffect(() => {
		if (deviceId === '') return;
		let cancelled = false;
		stoppedRef.current = false;

		navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: deviceId } } })
			.then((stream) => {
				if (cancelled) {
					stream.getTracks().forEach((t) => t.stop());
					return;
				}
				if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
				streamRef.current = stream;
				if (videoRef.current) {
					videoRef.current.srcObject = stream;
					videoRef.current.play();
				}
				setError('');
				rafRef.current = requestAnimationFrame(tick);
			})
			.catch((e) => {
				setError('Kamera konnte nicht geöffnet werden (' + e.message + ').');
			});

		return () => {
			cancelled = true;
			stoppedRef.current = true;
			if (rafRef.current) cancelAnimationFrame(rafRef.current);
			if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deviceId]);

	function tick() {
		if (stoppedRef.current) return;
		const video = videoRef.current;
		const canvas = canvasRef.current;
		if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
			canvas.width = video.videoWidth;
			canvas.height = video.videoHeight;
			const ctx = canvas.getContext('2d', { willReadFrequently: true });
			ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
			const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
			const code = jsQR(imageData.data, imageData.width, imageData.height);
			if (code) {
				handleScan(code.data);
				return;
			}
		}
		rafRef.current = requestAnimationFrame(tick);
	}

	function handleScan(text) {
		try {
			const parsed = parseGirocode(text);
			stoppedRef.current = true;
			if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
			props.onScan(parsed);
		} catch (e) {
			setError(e.message);
			rafRef.current = requestAnimationFrame(tick);
		}
	}

	function close() {
		props.onClose();
	}

	return (
		<div className="banking-sync-overlay">
			<div className="banking-sync-modal girocode-scan-modal">
				<div className="banking-sync-header">
					<span>Girocode scannen</span>
					<i className="bi bi-x-lg table-icon" onClick={close}></i>
				</div>
				<div className="banking-sync-body">
					{devices && devices.length > 1 &&
						<div className="girocode-device-switch">
							<label htmlFor="girocode-device-select">Kamera</label>
							<select
								id="girocode-device-select"
								value={deviceId}
								onChange={(e) => setDeviceId(e.target.value)}
							>
								{devices.map((d, i) => (
									<option key={d.deviceId} value={d.deviceId}>{d.label || 'Kamera ' + (i + 1)}</option>
								))}
							</select>
						</div>
					}
					<div className="girocode-video-wrap">
						<video ref={videoRef} className="girocode-video" playsInline muted></video>
						<canvas ref={canvasRef} className="girocode-canvas"></canvas>
					</div>
					{error !== '' && <p className="banking-sync-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
					<p className="banking-sync-hint"><i className="bi bi-info-circle"></i> Girocode auf der Rechnung in den Rahmen halten.</p>
				</div>
			</div>
		</div>
	)

};
